<template>
  <PopupBase
    @close-pop-up="handleCloseAttempt"
    class="ccrs-disclaimer"
    v-bind="$attrs"
  >
    <div
      class="ccrs-disclaimer__content"
      @keydown.esc.stop.prevent
    >
      <h3>
        {{ i18n.tv('disclaimerHeadline', 'hydraulicSelector') }}
      </h3>
      <Markdown
        :text="disclaimerText"
        class="ccrs-disclaimer__text"
      />
      <div class="ccrs-disclaimer__actions">
        <Checkbox
          :label="i18n.tv('acceptDisclaimerLabel', 'hydraulicSelector')"
          @change="acceptedDisclaimer = !acceptedDisclaimer"
          :disabled="!checkboxReady"
        />
        <ReactiveButton
          :button-text="i18n.tv('accept', 'hydraulicSelector')"
          :disabled="disableButton"
          @click="acceptAndClose"
        />
      </div>
    </div>
  </PopupBase>
</template>

<script setup lang="ts">
import PopupBase from './PopupBase.vue'
import Checkbox from '~/components/input-components/Checkbox'
import ReactiveButton from '~/components/ReactiveButton'
import Markdown from '~/components/Markdown.vue'
import {useConfigurationDataStore} from '@/store/configuration-data'
import useI18nExtended from '~/composables/usei18nExtended'
import useBreadcrumb from '~/composables/useBreadcrumb'

const i18n = useI18nExtended()

const acceptedDisclaimer = ref(false)
const checkboxReady = ref(false)

const disableButton = computed(() => {
  return !acceptedDisclaimer.value
})

const disclaimerText = computed(() => {
  return i18n.tv('disclaimerText', 'ccrs')
})

// Only close and save if the disclaimer was accepted
const handleCloseAttempt = () => {
  // Ignore close attempts (ESC, outside click) if not accepted
  if (!acceptedDisclaimer.value) {
    return
  }
  acceptAndClose()
}

const acceptAndClose = () => {
  if (acceptedDisclaimer.value) {
    useConfigurationDataStore().acceptedCcrsDisclaimer = true
  }
}

useBreadcrumb([{label: i18n.tv('title', 'ccrs')}])

onMounted(() => {
  checkboxReady.value = true
})
</script>

<style lang="scss">
.ccrs-disclaimer {
  &__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: left;
    max-width: 100%;
    padding: 0;

    // Mobile optimization: ensure proper spacing and readability
    @include breakpoint-down($md) {
      padding: 0 0.5rem;
    }

    h3 {
      margin: 0;
    }
  }

  &__text {
    margin: 1rem 0;
    line-height: 1.6;

    p {
      margin: 0.5rem 0;
    }

    ul, ol {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }

    strong, b {
      font-weight: $base-text-font-weight-semi-bold;
    }
  }

  &__actions {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    // Mobile optimization: stack vertically to prevent label overflow and button being pushed off-screen
    @include breakpoint-down($md) {
      flex-direction: column;
      align-items: stretch;
      gap: 1.25rem;

      // Ensure checkbox takes full width
      .enext-input--checkbox {
        width: 100%;
        max-width: 100%;
      }

      // Ensure touch-friendly button size
      button {
        min-height: 2.75rem;
        width: 100%;
      }
    }
  }

  // Scoped checkbox styles to avoid impacting other forms
  .enext-input--checkbox {
    width: 100%;
    max-width: 100%;

    .checkbox-wrapper {
      width: 100%;
      max-width: 100%;
      align-items: flex-start;
      flex-wrap: wrap;

      .k-checkbox {
        & + .k-checkbox-label {
          padding-left: $default-margin-padding-width / 2;
          margin-left: 0;
          word-break: break-word;
          overflow-wrap: break-word;
          hyphens: auto;
          flex: 1;
          max-width: 100%;
        }
      }
    }
  }

  .popup > .popup__wrapper > .popup__content > .close {
    display: none;
  }
}

body:has(.ccrs-disclaimer.popup--visible) {
  overflow: hidden;
}
</style>
